import { queryClient } from '~/app/providers/http'
import { FIVE_MINUTES_IN_MS } from '~/constants'
import { EMPTY_SESSION } from '~/features/auth/constants'
import { TSessionDetails } from '~/features/auth/models'
import { ECognitoGroups } from '~/models/auth'
import AuthService from '~/services/auth'
import { authSessionQueryKey } from '~/utils/auth'

type TAuthSession = Awaited<ReturnType<typeof AuthService.fetchAuthSession>>

export const queryKeys = {
  authSession: authSessionQueryKey,
}

const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object') return ''
  return 'message' in error ? String(error.message) : ''
}

const resolveAuthError = (
  error: unknown,
): { name: string; message: string } => {
  const candidate =
    error && typeof error === 'object' && 'error' in error
      ? (error as { error: unknown }).error
      : error

  const name =
    candidate && typeof candidate === 'object' && 'name' in candidate
      ? String(candidate.name)
      : ''

  return { name, message: getErrorMessage(candidate) }
}

const isAccessTokenExpiredError = (error: unknown) =>
  resolveAuthError(error).message.includes('Access Token has expired')

const USER_POOL_REFRESH_DEAD_NAMES = [
  'UserUnAuthenticatedException',
  'TokenRevokedException',
  'RefreshTokenReuseException',
]

const isUserPoolRefreshDeadError = (error: unknown) => {
  const { name, message } = resolveAuthError(error)

  if (
    USER_POOL_REFRESH_DEAD_NAMES.some((deadName) => name.startsWith(deadName))
  ) {
    return true
  }

  return (
    message.includes('Refresh Token has expired') ||
    message.includes('Refresh Token has been revoked') ||
    message.includes('Invalid Refresh Token')
  )
}

const hasLastAuthUser = () =>
  Object.keys(localStorage).some(
    (key) => key.endsWith('.LastAuthUser') && !!localStorage.getItem(key),
  )

export const isDeadSessionError = (error: unknown) => {
  if (isAccessTokenExpiredError(error)) {
    return false
  }

  if (isUserPoolRefreshDeadError(error)) {
    return true
  }

  return !hasLastAuthUser()
}

export const toSessionDetails = (
  authSession: TAuthSession,
): TSessionDetails => ({
  groups:
    (authSession?.tokens?.idToken?.payload[
      'cognito:groups'
    ] as ECognitoGroups[]) ?? [],
  email: (authSession?.tokens?.idToken?.payload.email as string) ?? '',
  cognitoUid: authSession?.userSub ?? '',
  accessToken: authSession?.tokens?.accessToken?.toString() ?? '',
  accessTokenExpiresAt: authSession?.tokens?.accessToken?.payload.exp,
})

const getAuthSessionDetails = async (): Promise<TSessionDetails> => {
  try {
    return toSessionDetails(await AuthService.fetchAuthSession())
  } catch (e) {
    const error = e as Error

    if (isAccessTokenExpiredError(error)) {
      return toSessionDetails(await AuthService.fetchAuthSession(true))
    }

    if (isDeadSessionError(error)) {
      return EMPTY_SESSION
    }

    throw error
  }
}

export const queries = {
  authSession: () => ({
    queryKey: queryKeys.authSession(),
    queryFn: getAuthSessionDetails,
    staleTime: FIVE_MINUTES_IN_MS,
    retry: 3,
    refetchInterval: FIVE_MINUTES_IN_MS,
  }),
}

export const refreshAuthSessionQuery = () =>
  queryClient.fetchQuery({
    ...queries.authSession(),
    staleTime: 0,
  })
